import { ReactNode } from "react";
import TableHorizontalScrollContainer from "../../components/Organismos/Table/TableHorizontalScrollContainer";

export type ReferralVariant = "member" | "staff";

export const referralPageClass = "flex w-full flex-col gap-4 bg-white px-6 py-8 text-sm text-gray-font lg:px-10 lg:py-10";

export const referralPanelClass = "mb-4 flex min-h-0 w-full flex-col gap-3 rounded-md border border-gray-200 bg-white p-5 text-sm text-gray-font shadow-sm";

export const referralFieldClass = "w-full border border-gray-300 bg-white px-2 py-1 text-gray-font";

export function referralAccent(variant: ReferralVariant): { button: string; action: string; text: string; tabActive: string; label: string } {
  const staff = variant === "staff";
  return {
    button: staff
      ? "inline-flex items-center bg-Default px-4 py-2 font-semibold text-neutral hover:bg-accent-Default-dark"
      : "inline-flex items-center bg-primary px-4 py-2 font-semibold text-neutral hover:bg-primary-dark",
    action: staff
      ? "mr-2 inline-flex bg-Default px-2 py-1 text-xs font-semibold text-neutral hover:bg-accent-Default-dark"
      : "mr-2 inline-flex bg-primary px-2 py-1 text-xs font-semibold text-neutral hover:bg-primary-dark",
    text: staff ? "font-semibold text-Default" : "font-semibold text-primary",
    tabActive: staff ? "border-b-2 border-Default text-Default" : "border-b-2 border-primary text-primary",
    label: staff ? "mb-1 block font-semibold text-Default" : "mb-1 block font-semibold text-primary",
  };
}

export const referralFormTitleRuleClass = "border-b border-gray-200 pb-[30px]";

export const referralFormGridThreeClass = "grid grid-cols-1 gap-x-[30px] gap-y-10 md:grid-cols-3";

export const referralFormGridTwoClass = "grid grid-cols-1 gap-x-[30px] gap-y-6 md:grid-cols-2";

export const referralFormFieldClass = "h-12 w-full border-0 bg-gray-25 px-5 py-[11.5px] font-roboto text-[17px] leading-[25.5px] text-gray-font disabled:opacity-60";

export const referralFormTextareaClass = "mb-5 h-[250px] w-full border border-gray-100 bg-white p-5 font-roboto text-[17px] leading-[25.5px] text-gray-700";

export const referralFormCheckboxClass = "flex items-center gap-1.5 font-roboto text-[17px] leading-[25.5px] text-gray-font";

export const referralFormHintClass = "ml-2 text-xs font-normal leading-[18px] text-feedback-success-dark";

export const referralFormActionsClass = "flex flex-wrap justify-end gap-[30px]";

export const referralFormCancelClass = "inline-flex h-[47px] w-[200px] items-center justify-between bg-gray-font pb-3 pl-[22px] pr-[22px] pt-[14px] font-decimal text-[14px] font-semibold uppercase leading-[21px] text-white hover:bg-neutral-dark";

export function referralFormAccent(variant: ReferralVariant): { title: string; note: string; section: string; label: string; required: string; submit: string } {
  const staff = variant === "staff";
  return {
    title: `font-decimal text-[38px] font-medium leading-[43.7px] ${staff ? "text-Default" : "text-primary"}`,
    note: `mb-10 mt-[50px] font-roboto text-[17px] font-medium leading-[25.5px] ${staff ? "text-Default" : "text-primary"}`,
    section: `block font-roboto text-[17px] font-medium uppercase leading-[25.5px] ${staff ? "text-Default" : "text-primary"}`,
    label: `mb-[10px] block font-roboto text-[17px] font-medium leading-[25.5px] ${staff ? "text-Default" : "text-accent-Default-dark"}`,
    required: staff ? "font-normal text-Default" : "font-normal text-primary",
    submit: staff
      ? "inline-flex h-[48.5px] w-[200px] items-center justify-between bg-Default pb-3 pl-[22px] pr-[22px] pt-[14px] font-decimal text-[15px] font-semibold uppercase leading-[22.5px] text-white hover:bg-accent-Default-dark"
      : "inline-flex h-[48.5px] w-[200px] items-center justify-between bg-primary-dark pb-3 pl-[22px] pr-[22px] pt-[14px] font-decimal text-[15px] font-semibold uppercase leading-[22.5px] text-white hover:bg-primary",
  };
}

export function ReferralPanel({ children }: { children: ReactNode }) {
  return <div className={referralPanelClass}>{children}</div>;
}

export function ReferralLoading() {
  return (
    <ReferralPanel>
      <div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
      <div className="h-32 animate-pulse rounded bg-gray-100" />
    </ReferralPanel>
  );
}

export function ReferralTable({ measureKey, children, className }: { measureKey: string | number; children: ReactNode; className?: string }) {
  return (
    <TableHorizontalScrollContainer measureKey={measureKey}>
      <table className={className || "min-w-full border-collapse text-sm text-gray-font"}>{children}</table>
    </TableHorizontalScrollContainer>
  );
}

export function referralRowClass(index: number): string {
  return index % 2 === 0 ? "border-b border-gray-100 bg-white" : "border-b border-gray-100 bg-gray-25";
}

export const referralHeadClass = "sticky top-0 z-10 border-b border-gray-200 bg-white";

export const referralCellClass = "px-2 py-2 text-left align-top";

export const referralStaffHeaderClass = "flex flex-wrap items-center justify-between gap-3 bg-gray-100 px-4 py-3";

export const referralStaffTitleClass = "text-xs font-bold uppercase leading-[18px] text-input-gray";

export const referralStaffAddClass = "inline-flex items-center bg-accent-Default-dark px-3 py-2 text-[10px] font-bold uppercase leading-[18px] text-neutral hover:bg-Default";

export const referralStaffLegendClass = "flex flex-wrap items-center justify-end gap-4 text-xs leading-[18px] text-input-gray";

export const referralStaffToolbarClass = "flex flex-wrap items-center justify-between gap-3 text-xs leading-[18px] text-input-gray";

export const referralStaffExportClass = "border border-gray-200 bg-white px-3 py-1.5 text-xs leading-[18px] text-neutral-dark hover:bg-gray-100";

export const referralStaffTableClass = "min-w-full border-collapse text-xs leading-[18px] text-input-gray";

export const referralStaffHeadCellClass = "px-2.5 py-2 text-left align-bottom font-bold";

export const referralStaffCellClass = "px-2.5 py-2 text-left align-top";

export const referralStaffPartyClass = "text-secondary-light";

export const referralStaffDetailTableClass = "w-auto border-separate border-spacing-x-0 border-spacing-y-2 font-[Arial,sans-serif] text-xs leading-[18px] text-gray-700";

export const referralStaffDetailLabelClass = "w-[120px] p-0 pl-[10px] text-left align-top font-bold text-neutral-dark";

export const referralStaffDetailValueClass = "p-0 text-left align-top";

export const referralStaffIconActionClass = "mr-1 inline-flex h-5 w-5 items-center justify-center text-Default hover:text-accent-Default-dark";

export const referralStaffPagerIdleClass = "inline-flex h-[30px] min-w-[30px] items-center justify-center border border-gray-200 bg-white px-3 py-1.5 text-xs leading-[18px] text-neutral-dark disabled:opacity-40";

export const referralStaffPagerActiveClass = "inline-flex h-[30px] min-w-[30px] items-center justify-center border border-neutral-dark bg-neutral-dark px-3 py-1.5 text-xs leading-[18px] text-white";

export const referralStaffCloseClass = "text-xs font-bold leading-[18px] text-accent-Default-dark hover:underline";

export const referralStaffFormClass = "flex flex-col gap-4 text-xs leading-[18px] text-gray-700";

export const referralStaffFormTopClass = "flex flex-wrap items-center justify-between gap-4";

export const referralStaffRequiredNoteClass = "font-bold text-accent-Default-dark";

export const referralStaffFormRowClass = "grid grid-cols-[110px_minmax(0,1fr)] items-start justify-items-start gap-x-6";

export const referralStaffPartyRowClass = "ml-[77px] grid grid-cols-[40px_minmax(0,1fr)] items-center justify-items-start gap-x-2";

export const referralStaffHintClass = "block text-xs font-normal leading-[18px] text-feedback-success-dark";

export const referralStaffFormActionsClass = "mx-auto mt-6 flex w-2/3 justify-center border-t border-gray-100 pt-5";

export const referralStaffSubmitClass = "inline-flex items-center bg-accent-Default-dark px-[15px] py-[5px] text-[10px] font-normal uppercase leading-[18px] text-neutral hover:bg-Default";

export function referralStaffFormLabelClass(required: boolean): string {
  return required ? "text-accent-Default-dark" : "text-gray-700";
}

export const referralMemberPageClass = "flex w-full flex-col bg-white px-6 py-8 text-gray-font lg:px-10 lg:py-10";

export const referralMemberBackClass = "mb-[30px] inline-flex w-fit items-center gap-1.5 font-decimal text-[15px] font-bold leading-[22.5px] text-accent-Default-dark";

export const referralMemberTitleClass = "mb-[30px] font-decimal text-[38px] font-medium leading-[43.7px] text-primary";

export const referralMemberDetailTableClass = "mx-auto w-4/5 border-collapse text-base leading-[normal] text-gray-font";

export const referralMemberDetailCellClass = "p-0 text-left align-top";

export const referralMemberCopyClass = "mb-5 text-[17px] leading-[25.5px]";

export const referralMemberSubmitClass = "inline-flex w-[378px] max-w-full items-center justify-between gap-3 bg-primary-dark pb-3 pl-[22px] pr-[22px] pt-[14px] font-decimal text-[15px] font-semibold uppercase leading-[22.5px] text-white hover:bg-primary";

export const referralMemberSubmitLabelClass = "w-[15.5rem] max-w-full";

export const referralMemberSectionClass = "mb-5 bg-accentDefault px-[25px] py-[15px] font-decimal text-[23px] font-medium uppercase leading-[34.5px] tracking-normal text-white";

export const referralMemberToolbarClass = "mb-5 flex flex-wrap items-center justify-between gap-3";

export const referralMemberExportClass = "text-[17px] font-bold leading-[17px] text-accent-Default-dark hover:underline";

export const referralMemberCountClass = "text-[17px] font-medium leading-[25.5px]";

export const referralMemberTableClass = "w-full table-fixed border-collapse text-base leading-[normal] text-gray-font";

export const referralMemberHeadClass = "bg-[#d8d8d8]";

export const referralMemberHeadEdgeClass = "bg-[#d8d8d8] px-5 py-[12.5px] text-left align-top font-decimal text-base font-semibold leading-[normal] text-gray-font";

export const referralMemberHeadMiddleClass = "bg-[#d8d8d8] py-[12.5px] pl-5 pr-0 text-left align-top font-decimal text-base font-semibold leading-[normal] text-gray-font";

export const referralMemberCellClass = "break-words px-5 py-[25px] text-left align-top text-base leading-[normal]";

export const referralMemberSortClass = "inline-flex items-center gap-2 text-left";

export const referralMemberPartyNameClass = "text-Default";

export const referralMemberPartyFirmClass = "block italic text-Default";

export const referralMemberActionClass = "mr-[5px] inline-flex h-[35px] min-w-[116px] items-center justify-center gap-2.5 bg-primary px-[15px] py-[7px] font-decimal text-[14px] font-semibold uppercase leading-[21px] text-neutral hover:bg-primary-dark";

export const referralMemberPagerActiveClass = "mx-[7px] inline-flex h-[25px] w-[25px] shrink-0 items-center justify-center rounded-full bg-Default p-0 text-[14px] font-black leading-[21px] text-white";

export const referralMemberPagerIdleClass = "mx-[7px] inline-flex h-[25px] w-[25px] shrink-0 items-center justify-center p-0 text-[17px] font-bold leading-[25.5px] text-accent-Default-dark";

export const referralMemberPagerGapClass = "mx-[7px] inline-flex h-[25px] w-[25px] shrink-0 items-center justify-center p-0 text-[17px] font-bold leading-[25.5px] text-black";
